// src/components/ResumeBuilder/forms/AchievementsForm.jsx
import React, { useContext } from "react";
import {
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import GradientButton from "../Button";
import { ResumeContext } from "../../ResumeContext";
import { saveResumeData } from "../../api/resumeapi";
import BasicInfoSkeleton from "./skeletonLoader";
import { AchievementsSchema } from "./resumevalidation";

export default function AchievementsForm({ onNext }) {
  const { data, setData, loading } = useContext(ResumeContext);

  //  Skeleton while loading
  if (loading || !data) {
    return <BasicInfoSkeleton />;
  }

  return (
    <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography fontSize={13} color="#6E6D81" mb={2}>
        You're meant for greater things — tell us your achievements so far
      </Typography>

      <Formik
        enableReinitialize
        initialValues={{
          achievements:
            Array.isArray(data?.achivementsArr) &&
            data.achivementsArr.length > 0
              ? data.achivementsArr
              : [{ title: "", description: "" }],
        }}
        validationSchema={AchievementsSchema}
        validateOnBlur
        validateOnChange
        onSubmit={async (values) => {
          const achivementsArr = values.achievements;

          const payload = {
            ...data,
            achivementsArr,
          };

          try {
            // SAVE TO DB
            await saveResumeData(payload);

            // UPDATE CONTEXT
            setData((prev) => ({
              ...prev,
              achivementsArr,
            }));
          } catch (err) {
            console.error("ACHIEVEMENTS SAVE ERROR:", err);
          }
        }}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          setTouched,
        }) => (
          <Form>
            <FieldArray name="achievements">
              {({ push, remove }) => (
                <Box>
                  {values.achievements.map((a, idx) => (
                    <Grid
                      container
                      spacing={2}
                      key={idx}
                      sx={{ mb: 1 }}
                      alignItems="center"
                    >
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label={`Achievement Title`}
                          name={`achievements[${idx}].title`}
                          size="small"
                          value={a.title}
                          onChange={handleChange}
                          error={
                            touched.achievements?.[idx]?.title &&
                            Boolean(errors.achievements?.[idx]?.title)
                          }
                          helperText={
                            touched.achievements?.[idx]?.title &&
                            errors.achievements?.[idx]?.title
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Short Description"
                          name={`achievements[${idx}].description`}
                          size="small"
                          value={a.description}
                          onChange={handleChange}
                          error={
                            touched.achievements?.[idx]?.description &&
                            Boolean(errors.achievements?.[idx]?.description)
                          }
                          helperText={
                            touched.achievements?.[idx]?.description &&
                            errors.achievements?.[idx]?.description
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }} textAlign="right">
                        <IconButton
                          onClick={() => remove(idx)}
                          disabled={values.achievements.length === 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      push({ title: "", description: "" })
                    }
                  >
                    Add achievement
                  </Button>
                </Box>
              )}
            </FieldArray>

            {/* BUTTONS */}
            <Box mt={3} display="flex" justifyContent="center" gap={4}>
              <GradientButton
                type="button"
                onClick={onNext}
              >
                Next
              </GradientButton>

              <GradientButton
                type="submit"
                onClick={() =>
                  setTouched(
                    {
                      achievements: values.achievements.map(() => ({
                        title: true,
                        description: true,
                      })),
                    },
                    true
                  )
                }
              >
                Save
              </GradientButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
}
